import { evaluateSupportReadonly } from './support-readonly-policy';
import type { SupportReadonlyEvaluation } from './support-readonly-policy';
import type { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';
import type { BranchQuoteSupportStatus } from '../../api/dealer-api-interface-franchisee';
import type { QuoteState } from '../../api/dealer-api-interface-quote';
import { html } from 'lit';

export interface SupportUserPermissions {
  canEdit: boolean;
  canChangeStatus: boolean;
  canReactivate: boolean;
}

export interface SupportCapabilityInputs {
  quoteManager: FranchiseeQuoteContainerManager | undefined;
  ticketStatus?: BranchQuoteSupportStatus | null;
  forceReadonly?: boolean; // component-level readonly (ticket closed, etc.)
  transientBusy?: boolean;
  phase?: 'inbox' | 'new' | 'editor' | 'eto';
  overridePermissions?: Partial<SupportUserPermissions>;
}

export interface SupportCapabilityResult extends SupportReadonlyEvaluation {
  systemOffline: boolean;
  resourceLocked: boolean;
  quoteState: QuoteState;
  ticketStatus?: BranchQuoteSupportStatus | null;
  permissions: SupportUserPermissions;
  phase?: string;
}

export function deriveSupportCapabilities(opts: SupportCapabilityInputs): SupportCapabilityResult {
  const qm = opts.quoteManager;
  const systemOffline = !!qm?.forceLockout;
  const resourceLocked = !systemOffline && !!qm?.lockInfo && !qm.lockInfo.isLockOwner;
  const quoteState: QuoteState = (qm?.quote?.state ?? (0 as any)) as QuoteState; // fallback safe draft default

  const baseEditable = !systemOffline && !resourceLocked && !opts.forceReadonly;
  const permissions: SupportUserPermissions = {
    canEdit: baseEditable,
    canChangeStatus: baseEditable, // refined per phase later
    canReactivate: baseEditable
  };
  if (opts.overridePermissions) Object.assign(permissions, opts.overridePermissions);

  const evaluation = evaluateSupportReadonly({
    quoteState,
    ticketStatus: opts.ticketStatus,
    systemOffline,
    resourceLocked,
    transientBusy: !!opts.transientBusy,
    userPermissions: permissions
  });

  return {
    ...evaluation,
    systemOffline,
    resourceLocked,
    quoteState,
    ticketStatus: opts.ticketStatus,
    permissions,
    phase: opts.phase
  };
}

export function capabilityReasonsKey(r: SupportCapabilityResult): string {
  return `${r.readonly}|${r.reasons.join(',')}|${r.actions.addNote}|${r.actions.changeStatus}|${r.actions.reactivate}`;
}

export function renderSupportDebugBanner(result: SupportCapabilityResult, label: string) {
  try {
    if (localStorage.getItem('support.debug.readonly') !== 'true') return html``;
  } catch {
    return html``;
  }
  if (!result.readonly && result.reasons.length === 0) return html``;
  const reasons = result.reasons.join(', ');
  return html`<div class="alert alert-info small mb-2">${label} Readonly: ${result.readonly} [${reasons}]</div>`;
}
